/**
 * pageContent.js - Choose which field of a Spotlight `pages/<slug>` record to render.
 *
 * A page record carries two content fields, and the platforms use them
 * differently. `body` is the canonical long-form copy the website shows;
 * `html` is what the TV/10-foot apps read, and for privacy-policy that is a QR
 * stub ("point your smartphone camera at the QR code") rather than the policy
 * itself. Rendering html on the web put a QR code — for the wrong brand and the
 * wrong document — on www.electricnow.tv/privacy-policy.
 *
 * Measured against prod for every slug the site renders, body is the fuller
 * source in each case (privacy-policy 2661 vs 155 chars of text, terms-of-use
 * 34920 vs 29833, about 3499 vs 1769, contact identical), so body-first is the
 * rule and html is only a fallback for a record that has no body.
 *
 * `body` is not consistently HTML: about's is markup, while privacy-policy,
 * terms-of-use and contact are plain text whose paragraphs are blank lines.
 * Piping plain text through dangerouslySetInnerHTML collapses it into one
 * unreadable block, so callers need to know which they got — hence `isHtml`.
 */

// A tag-like `<word` or `</word`. Deliberately not a bare `<`: legal copy says
// things like "under 13 < the age limit" and that must stay plain text.
const HTML_TAG_RE = /<\/?[a-z][a-z0-9]*[\s/>]/i;

const firstNonEmpty = (...values) => values.find(v => typeof v === 'string' && v.trim()) || null;

/**
 * @param {object|null|undefined} page - a Spotlight page record
 * @param {{ preferHtml?: boolean }} [options] - `preferHtml` flips the order for
 *   a record whose body is the worse copy. Only the `about` record needs it: its
 *   body contains the whole page twice (verified against prod — the opening
 *   sentence appears 2x, 3499 chars of text against html's clean 1769), so
 *   body-first renders About as a duplicated run-on. Drop the flag once that
 *   record is cleaned up in the CMS.
 * @returns {{ content: string|null, isHtml: boolean }} the content to render and
 *   whether it should be injected as markup (true) or rendered as text with
 *   newlines preserved (false).
 */
export const resolvePageContent = (page, { preferHtml = false } = {}) => {
  const content = preferHtml
    ? firstNonEmpty(page?.html, page?.body)
    : firstNonEmpty(page?.body, page?.html);
  return { content, isHtml: content ? HTML_TAG_RE.test(content) : false };
};

export default resolvePageContent;
